import { LIMITS } from "@aiyo/shared";
import { Button, toast } from "@heroui/react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckboxInput, FieldStack, TextInput } from "../components/fields";
import { InlineError, KeyValue, Page, PageHeader, Panel } from "../components/ui";
import { ApiError } from "../lib/api";
import { useChangePassword, useMe, useUpdateProfile } from "../lib/queries";

export function AccountPage() {
  const { t } = useTranslation();
  return (
    <Page>
      <PageHeader title={t("account.title")} description={t("account.subtitle")} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        <ProfilePanel />
        <PasswordPanel />
      </div>
    </Page>
  );
}

function ProfilePanel() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const update = useUpdateProfile();
  const [name, setName] = useState(me?.name ?? "");
  useEffect(() => setName(me?.name ?? ""), [me?.name]);
  if (!me) return null;
  const dirty = name.trim() !== me.name && name.trim().length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate({ name: name.trim() }, { onSuccess: () => toast.success(t("account.profileSaved")) });
  };

  return (
    <Panel title={t("account.profile")}>
      <form className="flex flex-col gap-6" onSubmit={submit}>
        <FieldStack>
          <TextInput
            name="name"
            label={t("account.name")}
            value={name}
            onChange={setName}
            isRequired
            autoComplete="name"
          />
        </FieldStack>
        <KeyValue
          items={[
            { label: t("account.email"), value: me.email },
            {
              label: t("account.role"),
              value: me.is_superadmin
                ? `${t(`account.roles.${me.role}`)} · ${t("nav.superadmin")}`
                : t(`account.roles.${me.role}`),
            },
            { label: t("account.organization"), value: me.org.name },
          ]}
        />
        <InlineError error={update.error} />
        <div className="flex justify-end">
          <Button type="submit" isDisabled={!dirty} isPending={update.isPending}>
            {t("common.saveChanges")}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function PasswordPanel() {
  const { t } = useTranslation();
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const min = LIMITS.passwordMinLength;

  const tooShort = next.length > 0 && next.length < min;
  const mismatch = confirm.length > 0 && confirm !== next;
  const serverCode = change.error instanceof ApiError ? change.error.code : null;
  const valid = current.length > 0 && next.length >= min && confirm === next;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!valid) return;
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          toast.success(t("account.passwordChanged"));
          setCurrent("");
          setNext("");
          setConfirm("");
          setSubmitted(false);
        },
      },
    );
  };

  const type = show ? "text" : "password";
  return (
    <Panel title={t("account.password")} description={t("account.passwordHint")}>
      <form className="flex flex-col gap-6" onSubmit={submit} noValidate>
        {/* Lets password managers attach the new password to the right account. */}
        <input type="text" name="username" autoComplete="username" value="" readOnly hidden />
        <FieldStack>
          <TextInput
            name="current_password"
            type={type}
            label={t("account.currentPassword")}
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            isRequired
            error={
              serverCode === "wrong_password"
                ? t("errors.codes.wrong_password")
                : submitted && !current
                  ? t("common.required")
                  : null
            }
          />
          <TextInput
            name="new_password"
            type={type}
            label={t("account.newPassword")}
            description={t("account.minLength", { count: min })}
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            isRequired
            error={
              tooShort || (submitted && next.length < min)
                ? t("account.tooShort", { count: min })
                : serverCode === "same_password"
                  ? t("errors.codes.same_password")
                  : null
            }
          />
          <TextInput
            name="confirm_password"
            type={type}
            label={t("account.confirmPassword")}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            isRequired
            error={mismatch || (submitted && confirm !== next) ? t("account.mismatch") : null}
          />
          <CheckboxInput label={t("account.show")} isSelected={show} onChange={setShow} />
        </FieldStack>
        {serverCode && serverCode !== "wrong_password" && serverCode !== "same_password" ? (
          <InlineError error={change.error} />
        ) : null}
        <div className="flex justify-end">
          <Button type="submit" isPending={change.isPending}>
            {t("account.changePassword")}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
